"use client";

import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Modal, Checkbox, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  CheckOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import Button from "../../../assets/AntDesign/Button";
import { ROUTES, DOCTOR_ROUTES } from "../../../routes/patientRoutes/routePaths";
import "./SignupPatient.css";
import "../AuthLayout.css";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import useSignupPatient from "../../hooks/useSignupPatient";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";

const SignupPatient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t }    = useTranslation();

  const {
    formData, setFormData,
    errors,
    showPrivacyModal, setShowPrivacyModal,
    privacyConfirmed, setPrivacyConfirmed,
    handleInputChange,
    handleGenderChange,
    handleShowPrivacyPopup,
    handleConfirmPrivacy,
    handleCreateAccount,
    localError,
  } = useSignupPatient();

  const password = formData.password || "";
  const passwordRules = {
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number:    /[0-9]/.test(password),
    special:   /[@$!%*?&]/.test(password),
  };

  /* helper — renders a field-level error the same way as LoginPatient */
  const FieldError = ({ id, msg }) =>
    msg ? (
      <span id={id} className="sp-field-error" role="alert">
        <ExclamationCircleFilled className="sp-field-error__icon" />
        {msg}
      </span>
    ) : null;

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? true : stored === "true"; // true = layout par défaut (hero gauche, form droite)
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour l'animation du swap (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsDoctor = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      // on rend le swap permanent : persistance + transmission à la page suivante
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginDoctor", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  return (
    <div className="signup-container signup-patient-page">
      <div
        className={`signup-background signup-doctor ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("signup.createAccountTitlePrefix", "Create Your")}{" "}
                <span className="signup-form-title-accent">
                  {t("signup.createAccountTitleAccentPatient", "Patient Account")}
                </span>
              </h1>
              <p className="signup-form-subtitle">
                {t("signup.createAccountSubtitle", "Create an account to access your space.")}
              </p>
            </div>

            <div className="signup-form-content">

              {/* ── API / general error banner ── */}
              {localError && (
                <div className="sp-error-banner" role="alert">
                  <ExclamationCircleFilled className="sp-error-banner__icon" />
                  <span>{localError}</span>
                </div>
              )}

              {/* ── First + Last name ── */}
              <div className="signup-form-row">
                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("commonn.firstName")} <span className="signup-required">*</span>
                  </label>
                  <Input
                    placeholder={t("commonn.firstNamePlaceholder")}
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    className="signup-form-input"
                    prefix={<UserOutlined />}
                    status={errors.firstName ? "error" : ""}
                    aria-invalid={!!errors.firstName}
                    aria-describedby={errors.firstName ? "sp-err-firstName" : undefined}
                  />
                  <FieldError id="sp-err-firstName" msg={errors.firstName} />
                </div>

                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("commonn.lastName")} <span className="signup-required">*</span>
                  </label>
                  <Input
                    placeholder={t("commonn.lastNamePlaceholder")}
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    className="signup-form-input"
                    prefix={<UserOutlined />}
                    status={errors.lastName ? "error" : ""}
                    aria-invalid={!!errors.lastName}
                    aria-describedby={errors.lastName ? "sp-err-lastName" : undefined}
                  />
                  <FieldError id="sp-err-lastName" msg={errors.lastName} />
                </div>
              </div>

              {/* ── Date of birth + Gender ── */}
              <div className="signup-form-row">
                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("signup.dateOfBirth")} <span className="signup-required">*</span>
                  </label>
                  <DatePicker
                    className="signup-form-input"
                    value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                    onChange={(_, dateString) => handleInputChange("dateOfBirth", dateString)}
                    format="YYYY-MM-DD"
                    placeholder={t("signup.dateOfBirthPlaceholder")}
                    style={{ width: "100%" }}
                    status={errors.dateOfBirth ? "error" : ""}
                    aria-invalid={!!errors.dateOfBirth}
                  />
                  <FieldError id="sp-err-dob" msg={errors.dateOfBirth} />
                </div>

                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("signup.gender")} <span className="signup-required">*</span>
                  </label>
                  <select
                    value={formData.gender || ""}
                    onChange={(event) => handleGenderChange(event.target.value)}
                    className={`signup-form-input signup-gender-select${
                      formData.gender ? " signup-gender-select--selected" : ""
                    }`}
                    aria-invalid={!!errors.gender}
                    aria-describedby={errors.gender ? "sp-err-gender" : undefined}
                  >
                    <option value="" disabled>
                      {t("signup.genderPlaceholder")}
                    </option>
                    <option value="male">{t("signup.male")}</option>
                    <option value="female">{t("signup.female")}</option>
                  </select>
                  <FieldError id="sp-err-gender" msg={errors.gender} />
                </div>
              </div>

              {/* ── Email ── */}
              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("commonn.email")} <span className="signup-required">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="signup-form-input"
                  prefix={<MailOutlined />}
                  placeholder={t("commonn.emailPlaceholder")}
                  status={errors.email ? "error" : ""}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "sp-err-email" : undefined}
                />
                <FieldError id="sp-err-email" msg={errors.email} />
              </div>

              {/* ── Phone ── */}
              <div className="signup-form-group">
                <label className="signup-form-label">
                  {t("commonn.phone")} <span className="signup-required">*</span>
                </label>
                <PhoneInput
                  country={"tn"}
                  enableSearch
                  value={formData.phoneNumber}
                  onChange={(phone) => handleInputChange("phoneNumber", phone)}
                  inputClass={`signup-phone-input${errors.phoneNumber ? " sp-phone--error" : ""}`}
                  containerClass="phone-input-container"
                  buttonClass="phone-input-flag"
                  specialLabel=""
                />
                <FieldError id="sp-err-phone" msg={errors.phoneNumber} />
              </div>

              {/* ── Password + Confirm ── */}
              <div className="signup-form-row">
                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("commonn.password")} <span className="signup-required">*</span>
                  </label>
                  <Input.Password
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className="signup-form-input"
                    prefix={<LockOutlined />}
                    placeholder={t("commonn.passwordPlaceholder")}
                    status={errors.password ? "error" : ""}
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? "sp-err-password" : undefined}
                  />
                  <FieldError id="sp-err-password" msg={errors.password} />

                  {/* password rules checklist */}
                  {password && !Object.values(passwordRules).every(rule => rule) && (
                    <ul className="signup-password-rules">
                      {[
                        { key: "minLength", label: t("signup.passwordRuleMinLength", "At least 8 characters") },
                        { key: "uppercase", label: t("signup.passwordRuleUppercase", "Uppercase letter") },
                        { key: "lowercase", label: t("signup.passwordRuleLowercase", "Lowercase letter") },
                        { key: "number",    label: t("signup.passwordRuleNumber",    "Number") },
                        { key: "special",   label: t("signup.passwordRuleSpecial",   "Special character (@$%!*&?)") },
                      ].map(({ key, label }) => (
                        <li
                          key={key}
                          className={`signup-password-rule ${
                            passwordRules[key]
                              ? "signup-password-rule-valid"
                              : "signup-password-rule-invalid"
                          }`}
                        >
                          {passwordRules[key] ? <CheckOutlined /> : <CloseOutlined />}
                          <span>{label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="signup-form-group signup-half-width">
                  <label className="signup-form-label">
                    {t("auth.confirmPassword")} <span className="signup-required">*</span>
                  </label>
                  <Input.Password
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className={`signup-form-input${
                      formData.confirmPassword &&
                      formData.password !== formData.confirmPassword
                        ? " signup-password-mismatch"
                        : ""
                    }`}
                    prefix={<LockOutlined />}
                    placeholder={t("auth.confirmPassword")}
                    status={errors.confirmPassword ? "error" : ""}
                    aria-invalid={!!errors.confirmPassword}
                    aria-describedby={errors.confirmPassword ? "sp-err-confirm" : undefined}
                  />
                  <FieldError id="sp-err-confirm" msg={errors.confirmPassword} />
                </div>
              </div>

              {/* ── Terms & Privacy ── */}
              <div className="signup-form-group signup-checkbox-group">
                <Checkbox
                  checked={formData.acceptTerms}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleShowPrivacyPopup();
                    } else {
                      setFormData((prev) => ({ ...prev, acceptTerms: false }));
                      setPrivacyConfirmed(false);
                    }
                  }}
                >
                  {t("signup.agreeToTerms")}{" "}
                  <span className="signup-terms-link" onClick={handleShowPrivacyPopup}>
                    {t("signup.termsAndConditions")}
                  </span>{" "}
                  {t("commonn.and")}{" "}
                  <span className="signup-terms-link" onClick={handleShowPrivacyPopup}>
                    {t("signup.privacyPolicy")}
                  </span>
                </Checkbox>
                <FieldError id="sp-err-terms" msg={errors.acceptTerms} />
              </div>

              {/* ── Create Account ── */}
              <div className="signup-form-group">
                <Button
                  onClick={handleCreateAccount}
                  className="signup-create-account-btn"
                  disabled={
                    !formData.acceptTerms ||
                    formData.password !== formData.confirmPassword
                  }
                >
                  {t("auth.signUpButton")}
                </Button>
              </div>

              {/* ── Sign-in redirect ── */}
              <div className="signup-login-link-wrapper">
               
                <span className="signup-login-text">
                  {t("signup.alreadyHaveAccount", "Already have an account?")} {" "}
                  <span className="signup-login-link" onClick={() => navigate("/LoginPatient")}>
                    {t("commonn.login", "Login")}
                  </span>
                </span>
              </div>

            </div>
          </div>
        </section>

        {/* ── Hero right ── */}
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" aria-hidden="true" />
          <button
            type="button"
            className="signup-back-home"
            onClick={() => navigate(ROUTES.LANDING ?? "/")}
            aria-label={t("common.backToHome", "Back to Home")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>
          <div className="signup-hero-inner">
            <h1 className="signup-hero-title">
              {t("signup.heroPanelTitle", "Are you joining as a healthcare professional?")}
            </h1>
            <p className="signup-hero-sub">
              {t("signup.heroPanelText", "This registration is designed for patients. If you are a doctor and wish to manage your practice, schedule appointments, and connect with patients, please ")}
              <strong>{t("signup.heroPanelTextStrong", "create a doctor account.")}</strong>
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsDoctor}
            >
              {t("signup.continueAsDoctor", "Continue as a doctor")}
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>
      </div>

      {/* ── Privacy Modal ── */}
      <Modal
        title={t("signup.privacyPolicyTitle")}
        open={showPrivacyModal}
        onCancel={() => setShowPrivacyModal(false)}
        onOk={handleConfirmPrivacy}
        okButtonProps={{ disabled: !privacyConfirmed }}
        okText={t("signup.privacyOkButton")}
        cancelText={t("commonn.cancel")}
        className="signup-privacy-modal"
      >
        <div className="privacy-text">
          <p>{t("signup.privacy.intro")}</p>
          <p>{t("signup.privacy.paragraph1")}</p>

          <h4>{t("signup.privacy.section1Title")}</h4>
          <p>{t("signup.privacy.section1Text")}</p>
          <ul>
            <li>{t("signup.privacy.section1Item1")}</li>
            <li>{t("signup.privacy.section1Item2")}</li>
            <li>{t("signup.privacy.section1Item3")}</li>
            <li>{t("signup.privacy.section1Item4")}</li>
          </ul>
          <p>{t("signup.privacy.section1Conclusion")}</p>

          <h4>{t("signup.privacy.section2Title")}</h4>
          <ul>
            <li>{t("signup.privacy.section2Item1")}</li>
            <li>{t("signup.privacy.section2Item2")}</li>
            <li>{t("signup.privacy.section2Item3")}</li>
            <li>{t("signup.privacy.section2Item4")}</li>
            <li>{t("signup.privacy.section2Item5")}</li>
          </ul>

          <h4>{t("signup.privacy.section3Title")}</h4>
          <ul>
            <li>{t("signup.privacy.section3Item1")}</li>
            <li>{t("signup.privacy.section3Item2")}</li>
            <li>{t("signup.privacy.section3Item3")}</li>
          </ul>
          <p>{t("signup.privacy.section3Conclusion")}</p>

          <h4>{t("signup.privacy.section4Title")}</h4>
          <ul>
            <li>{t("signup.privacy.section4Item1")}</li>
            <li>{t("signup.privacy.section4Item2")}</li>
            <li>{t("signup.privacy.section4Item3")}</li>
          </ul>

          <h4>{t("signup.privacy.section5Title")}</h4>
          <ul>
            <li>{t("signup.privacy.section5Item1")}</li>
            <li>{t("signup.privacy.section5Item2")}</li>
          </ul>

          <h4>{t("signup.privacy.section6Title")}</h4>
          <p>
            {t("signup.privacy.section6Text")}<br />
            📧 <a href="mailto:support@mediwave.tn">support@mediwave.tn</a>
          </p>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={privacyConfirmed}
              onChange={(e) => setPrivacyConfirmed(e.target.checked)}
            >
              {t("signup.privacy.confirmationCheckbox")}
            </Checkbox>
            <p style={{ marginTop: 8, color: "#595959" }}>
              {t("signup.privacy.confirmationNote")}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SignupPatient;
